import React, { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { logger } from '@/lib/logger';
import { Room, Point } from '@/types/floorPlanViewer';
import { RoomPolygon } from '@/components/floor-plans/components/RoomPolygon';
import { RoomDimensions } from '@/components/floor-plans/components/RoomDimensions';
import { useRoomState, useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { useFixturesStandardized } from '@/hooks/entities/useFixturesStandardized';
import { useControlsStandardized } from '@/hooks/entities/useControlsStandardized';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { getRoomTypeColor, formatRoomLabel } from '@/utils/segmentationUtils';
import { toast } from 'sonner';
import { useLVProducts } from '@/features/product-library/hooks/useLVProducts';

interface RoomLayerProps {
  rooms: Room[];
  scale: number;
  onStartDrag?: (
    type: 'fixture' | 'control' | 'panel' | 'room',
    id: string,
    e: React.MouseEvent,
  ) => void;
  isDragging?: boolean;
  dragState?: {
    type: string;
    id: string;
    startPos: { x: number; y: number };
    currentPos: { x: number; y: number };
    hasMovedThreshold: boolean;
  } | null;
  wasRecentlyDragged?: (id: string) => boolean;
}

export const RoomLayer = memo(
  ({ rooms, scale, onStartDrag, isDragging, dragState, wasRecentlyDragged }: RoomLayerProps) => {
    // Subscribe to specific values for proper reactivity with Zustand
    const roomConfidenceThreshold = useAppStore(state => state.aiTools.roomConfidenceThreshold);
    const roomOpacityFromStore = useAppStore(state => state.aiTools.roomOpacity);
    const roomState = useRoomState();
    const selectedRooms = useAppStore(state => state.selection.rooms);
    const toggleEntitySelection = useAppStore(state => state.toggleEntitySelection);
    const imageSize = useAppStore(state => state.floorPlan.imageSize);
    const { floorPlan, data } = useFloorPlanContext();
    const { updateRoom } = useRoomsStandardized(floorPlan?.id || '');
    const { updateFixture } = useFixturesStandardized(floorPlan?.id || '');
    const { updateControl } = useControlsStandardized(floorPlan?.id || '');
    const { updateProductAsync } = useLVProducts(floorPlan?.id || '');
    const { scaleData } = useFloorPlanScale(floorPlan?.id || '');

    // Track previously selected rooms to detect deselection
    const prevSelectedRoomsRef = useRef<Set<string>>(new Set(selectedRooms));

    // Accept both raw array or { data: Room[] }
    const roomArray: Room[] = Array.isArray(rooms) ? rooms : ((rooms as any)?.data ?? []);

    // Keep a ref to the current room array to avoid stale closures
    const roomArrayRef = useRef(roomArray);
    roomArrayRef.current = roomArray;

    // Track optimistic updates for smooth dragging
    const [optimisticRooms, setOptimisticRooms] = useState<Record<string, Room>>({});

    // Create a stable room IDs string to use as dependency
    const roomIdsString = useMemo(() => {
      return roomArray
        .map(r => r.id)
        .sort()
        .join(',');
    }, [roomArray]);

    // Clear optimistic updates when rooms data changes
    useEffect(() => {
      setOptimisticRooms(prev => {
        // If no optimistic updates, return early
        if (Object.keys(prev).length === 0) return prev;

        // Check if any optimistic room was deleted
        const currentRoomIds = new Set(roomArray.map(r => r.id));
        const hasDeletedRooms = Object.keys(prev).some(roomId => !currentRoomIds.has(roomId));

        if (hasDeletedRooms) {
          // Filter out deleted rooms
          const updated: Record<string, Room> = {};
          Object.keys(prev).forEach(roomId => {
            if (currentRoomIds.has(roomId)) {
              updated[roomId] = prev[roomId];
            }
          });
          return updated;
        }

        // No changes needed
        return prev;
      });
    }, [roomIdsString]); // Use stable string representation

    // Store pending updates to batch them
    const pendingUpdatesRef = useRef<Record<string, { points: Point[] }>>({});

    // Store original points when dragging starts
    const originalPointsRef = useRef<Record<string, Point[]>>({});

    // IMPORTANT: Use aiTools values directly, not roomState
    // The sliders update aiTools, not roomState
    const opacity = roomOpacityFromStore ?? 0.4;
    const threshold = roomConfidenceThreshold ?? 0; // 0-1 range

    // Get multi-selection state
    const selectedFixtures = useAppStore(state => state.selection.fixtures);
    const selectedControls = useAppStore(state => state.selection.controls);
    const selectedProducts = useAppStore(state => state.selection.products);
    const selectedAnnotations = useAppStore(state => state.selection.annotations);
    const totalSelections =
      selectedFixtures.size +
      selectedControls.size +
      selectedProducts.size +
      selectedAnnotations.size +
      selectedRooms.size;
    const isMultiSelection = totalSelections > 1;
    const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

    const handleRoomSelect = useCallback(
      (roomId: string, e?: React.MouseEvent) => {
        // Don't select if we just finished dragging this room
        if (wasRecentlyDragged && wasRecentlyDragged(roomId)) {
          return;
        }

        // Check for shift key for multi-selection
        if (e && e.shiftKey) {
          // Multi-select with shift key
          toggleEntitySelection('room', roomId);
          // Check if we now have multiple selections
          const newTotalSelections = totalSelections + (selectedRooms.has(roomId) ? -1 : 1);

          // Only skip drawer if ALL selections are annotations
          const state = useAppStore.getState();
          const annotationCount = state.selection.annotations.size;
          const hasOnlyAnnotations = annotationCount > 0 && annotationCount === newTotalSelections;

          if (newTotalSelections > 1 && !hasOnlyAnnotations) {
            setActiveDrawer('selection');
          }
        } else if (isMultiSelection && selectedRooms.has(roomId)) {
          // Already selected in multi-selection, just toggle it
          toggleEntitySelection('room', roomId);
        } else {
          // Single select
          useAppStore.getState().clearEntitySelection('room');
          useAppStore.getState().selectEntity('room', roomId);
          // Only open drawer if not in multi-selection mode
          if (!isMultiSelection) {
            setActiveDrawer('room');
          }
        }
      },
      [
        wasRecentlyDragged,
        toggleEntitySelection,
        selectedRooms,
        totalSelections,
        isMultiSelection,
        setActiveDrawer,
      ],
    );

    const handleRoomDoubleClick = useCallback(
      (roomId: string) => {
        // First ensure the room is selected
        if (!selectedRooms.has(roomId)) {
          useAppStore.getState().clearEntitySelection('room');
          useAppStore.getState().selectEntity('room', roomId);
        }

        // Set the active drawer to room and notify that we want to focus the name field
        useAppStore.getState().setActiveDrawer('room');

        // Use a small timeout to ensure the drawer is open before trying to focus
        setTimeout(() => {
          // Dispatch a custom event that the drawer can listen to
          window.dispatchEvent(new CustomEvent('focusRoomName', { detail: { roomId } }));
        }, 100);
      },
      [selectedRooms],
    );

    // Validate polygon (ensure no self-intersections)
    const validatePolygon = (points: Point[]): boolean => {
      if (points.length < 3) return false;

      // Check for self-intersections (simplified check)
      // In a production app, you'd want a more robust intersection algorithm
      const area = points.reduce((sum, p, i) => {
        const next = points[(i + 1) % points.length];
        return sum + (p.x * next.y - next.x * p.y);
      }, 0);

      // If area is 0 or very small, polygon is invalid
      return Math.abs(area) > 1;
    };

    // Helper function to check if a point is inside a polygon
    const isPointInPolygon = useCallback((point: Point, polygon: Point[]): boolean => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x,
          yi = polygon[i].y;
        const xj = polygon[j].x,
          yj = polygon[j].y;
        const intersect =
          yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    }, []);

    // Check and update all elements affected by room movement
    const checkAndUpdateElementsAfterRoomMove = useCallback(
      async (roomId: string, newPoints: Point[]) => {
        if (!data) return;

        const updates: Array<{
          type: 'fixture' | 'control' | 'product';
          id: string;
          update: { room_id: string | null };
        }> = [];

        // PART 1: Elements that were IN this room - check if they're still inside
        const fixturesInRoom = data.fixtures?.filter(f => f.room_id === roomId) || [];
        const controlsInRoom = data.controls?.filter(c => c.room_id === roomId) || [];
        const productsInRoom = data.lvProducts?.filter(p => p.room_id === roomId) || [];

        // Remove room assignment for elements now outside
        fixturesInRoom
          .filter(
            fixture =>
              !isPointInPolygon({ x: fixture.x_position, y: fixture.y_position }, newPoints),
          )
          .forEach(fixture =>
            updates.push({
              type: 'fixture',
              id: fixture.id,
              update: { room_id: null },
            }),
          );

        controlsInRoom
          .filter(control => !isPointInPolygon({ x: control.x, y: control.y }, newPoints))
          .forEach(control =>
            updates.push({
              type: 'control',
              id: control.id,
              update: { room_id: null },
            }),
          );

        productsInRoom
          .filter(
            product =>
              !isPointInPolygon({ x: product.x_position, y: product.y_position }, newPoints),
          )
          .forEach(product =>
            updates.push({
              type: 'product',
              id: product.id,
              update: { room_id: null },
            }),
          );

        // PART 2: Elements that were NOT in this room - check if they're now inside
        const fixturesNotInRoom = data.fixtures?.filter(f => f.room_id !== roomId) || [];
        const controlsNotInRoom = data.controls?.filter(c => c.room_id !== roomId) || [];
        const productsNotInRoom = data.lvProducts?.filter(p => p.room_id !== roomId) || [];

        // Add room assignment for elements now inside
        fixturesNotInRoom
          .filter(fixture =>
            isPointInPolygon({ x: fixture.x_position, y: fixture.y_position }, newPoints),
          )
          .forEach(fixture =>
            updates.push({
              type: 'fixture',
              id: fixture.id,
              update: { room_id: roomId },
            }),
          );

        controlsNotInRoom
          .filter(control => isPointInPolygon({ x: control.x, y: control.y }, newPoints))
          .forEach(control =>
            updates.push({
              type: 'control',
              id: control.id,
              update: { room_id: roomId },
            }),
          );

        productsNotInRoom
          .filter(product =>
            isPointInPolygon({ x: product.x_position, y: product.y_position }, newPoints),
          )
          .forEach(product =>
            updates.push({
              type: 'product',
              id: product.id,
              update: { room_id: roomId },
            }),
          );

        // Execute all updates
        if (updates.length > 0) {
          try {
            const promises = updates.map(({ type, id, update }) => {
              switch (type) {
                case 'fixture':
                  return updateFixture(id, update);
                case 'control':
                  return updateControl(id, update);
                case 'product':
                  return updateProductAsync({ id, updates: update });
                default:
                  return Promise.resolve();
              }
            });

            await Promise.all(promises);

            const added = updates.filter(u => u.update.room_id === roomId).length;
            const removed = updates.filter(u => u.update.room_id === null).length;

            logger.log(`Room ${roomId} move: ${added} elements added, ${removed} elements removed`);

            if (added > 0 || removed > 0) {
              const message =
                added > 0 && removed > 0
                  ? `${added} element${added > 1 ? 's' : ''} added, ${removed} removed from room`
                  : added > 0
                    ? `${added} element${added > 1 ? 's' : ''} added to room`
                    : `${removed} element${removed > 1 ? 's' : ''} removed from room`;
              toast.success(message);
            }
          } catch (error) {
            logger.error('Error updating elements after room move:', error);
            toast.error('Failed to update some elements');
          }
        }
      },
      [data, isPointInPolygon, updateFixture, updateControl, updateProductAsync],
    );

    // Debounced update function
    const debouncedUpdateRoom = useDebouncedCallback(async (roomId: string) => {
      const updates = pendingUpdatesRef.current[roomId];
      if (!updates) return;

      try {
        await updateRoom(roomId, updates);

        // Check and update all elements affected by the room move
        await checkAndUpdateElementsAfterRoomMove(roomId, updates.points);

        // Clear pending update after successful save
        delete pendingUpdatesRef.current[roomId];

        // Don't clear optimistic update here - let the refetch handle it
        // This prevents the room from "jumping" back to original position
      } catch (error) {
        logger.error('Error updating room:', error);
        toast.error('Failed to update room');
        // Revert optimistic update on error
        setOptimisticRooms(prev => {
          const updated = { ...prev };
          delete updated[roomId];
          return updated;
        });
        delete pendingUpdatesRef.current[roomId];
      }
    }, 300);

    // Detect room deselection and save pending changes
    useEffect(() => {
      const currentSelectedSet = new Set(selectedRooms);
      const prevSelectedSet = prevSelectedRoomsRef.current;

      // Find rooms that were deselected
      prevSelectedSet.forEach(roomId => {
        if (!currentSelectedSet.has(roomId) && pendingUpdatesRef.current[roomId]) {
          // Room was deselected and has pending updates - save them
          debouncedUpdateRoom(roomId);
        }
      });

      // Update the ref for next comparison
      prevSelectedRoomsRef.current = currentSelectedSet;
    }, [selectedRooms, debouncedUpdateRoom]);

    // Handle vertex dragging
    const handlePointDrag = useCallback(
      (roomId: string) => (pointIndex: number, newPoint: Point) => {
        const room = roomArrayRef.current.find(r => r.id === roomId);
        if (!room) {
          logger.error('Room not found for point drag:', roomId);
          return;
        }

        // Create optimistic update
        let currentPoints = optimisticRooms[roomId]?.points || room.points;
        // Parse points if they're strings
        if (typeof currentPoints === 'string') {
          try {
            currentPoints = JSON.parse(currentPoints);
          } catch (e) {
            logger.error('Failed to parse room points:', e);
            return;
          }
        }
        const updatedPoints = [...currentPoints];
        updatedPoints[pointIndex] = newPoint;

        // Validate polygon
        if (!validatePolygon(updatedPoints)) {
          toast.error('Invalid room shape');
          return;
        }

        setOptimisticRooms(prev => ({
          ...prev,
          [roomId]: { ...room, points: updatedPoints },
        }));

        // Store pending update but DON'T trigger save yet
        pendingUpdatesRef.current[roomId] = { points: updatedPoints };
        // Don't call debouncedUpdateRoom here - wait for deselection
      },
      [optimisticRooms],
    );

    // Handle adding new vertex
    const handleAddPoint = useCallback(
      (roomId: string) => (pointIndex: number, newPoint: Point) => {
        const room = roomArrayRef.current.find(r => r.id === roomId);
        if (!room) return;

        // Get current points
        let currentPoints = optimisticRooms[roomId]?.points || room.points;
        // Parse points if they're strings
        if (typeof currentPoints === 'string') {
          try {
            currentPoints = JSON.parse(currentPoints);
          } catch (e) {
            logger.error('Failed to parse room points:', e);
            return;
          }
        }

        // Insert new point at the specified index
        const updatedPoints = [...currentPoints];
        updatedPoints.splice(pointIndex, 0, newPoint);

        // Validate polygon
        if (!validatePolygon(updatedPoints)) {
          toast.error('Invalid room shape');
          return;
        }

        // Immediate update with optimistic UI
        setOptimisticRooms(prev => ({
          ...prev,
          [roomId]: { ...room, points: updatedPoints },
        }));

        // Store pending update but don't save yet
        pendingUpdatesRef.current[roomId] = { points: updatedPoints };
      },
      [optimisticRooms],
    );

    // Handle removing vertex
    const handleRemovePoint = useCallback(
      (roomId: string) => (pointIndex: number) => {
        const room = roomArrayRef.current.find(r => r.id === roomId);
        if (!room) return;

        // Get current points
        let currentPoints = optimisticRooms[roomId]?.points || room.points;
        // Parse points if they're strings
        if (typeof currentPoints === 'string') {
          try {
            currentPoints = JSON.parse(currentPoints);
          } catch (e) {
            logger.error('Failed to parse room points:', e);
            return;
          }
        }

        // Don't allow removal if only 3 points left (minimum for polygon)
        if (currentPoints.length <= 3) {
          toast.error('Cannot remove vertex - polygon must have at least 3 points');
          return;
        }

        // Remove point at the specified index
        const updatedPoints = currentPoints.filter((_, index) => index !== pointIndex);

        // Validate polygon
        if (!validatePolygon(updatedPoints)) {
          toast.error('Invalid room shape');
          return;
        }

        // Immediate update with optimistic UI
        setOptimisticRooms(prev => ({
          ...prev,
          [roomId]: { ...room, points: updatedPoints },
        }));

        // Store pending update but don't save yet
        pendingUpdatesRef.current[roomId] = { points: updatedPoints };
      },
      [optimisticRooms],
    );

    // Handle room drag using drag handler
    const handleRoomMouseDown = useCallback(
      (roomId: string) => (e: React.MouseEvent) => {
        if (onStartDrag) {
          // Let the drag handler manage the dragging
          onStartDrag('room', roomId, e);
        }
      },
      [onStartDrag],
    );

    // Update room position during drag
    useEffect(() => {
      if (isDragging && dragState && dragState.type === 'room') {
        const roomId = dragState.id;
        const room = roomArrayRef.current.find(r => r.id === roomId);
        if (!room) return;

        // Get current points - use optimistic update if available
        let currentPoints = optimisticRooms[roomId]?.points || room.points;
        // Parse points if they're strings
        if (typeof currentPoints === 'string') {
          try {
            currentPoints = JSON.parse(currentPoints);
          } catch (e) {
            logger.error('Failed to parse room points:', e);
            return;
          }
        }

        // Store original points on first drag - use the current position (which may include previous drags)
        if (!originalPointsRef.current[roomId]) {
          originalPointsRef.current[roomId] = [...currentPoints];
        }

        // Calculate delta from original position
        const deltaX = dragState.currentPos.x - dragState.startPos.x;
        const deltaY = dragState.currentPos.y - dragState.startPos.y;

        // Move all points by the delta from original
        const updatedPoints = originalPointsRef.current[roomId].map(point => ({
          x: point.x + deltaX,
          y: point.y + deltaY,
        }));

        // Update optimistic state
        setOptimisticRooms(prev => ({
          ...prev,
          [roomId]: { ...room, points: updatedPoints },
        }));

        // Store pending update
        pendingUpdatesRef.current[roomId] = { points: updatedPoints };
      }
    }, [isDragging, dragState]);

    // Handle drag end - just clear the reference, don't save
    useEffect(() => {
      if (!isDragging && originalPointsRef.current) {
        // Just clear original points reference - saving happens on deselection
        originalPointsRef.current = {};
      }
    }, [isDragging]);

    // Remove useMemo filtering - we'll filter inside the map loop like FixtureLayer does

    // Render a single SVG that contains all polygons so we don't create many SVG roots.
    // Make the SVG canvas much larger than the image to allow dragging outside
    const svgDimensions = useMemo(() => {
      const svgPadding = 5000; // Large padding to allow dragging far outside the image
      const svgWidth = (imageSize.width || 1000) + svgPadding * 2;
      const svgHeight = (imageSize.height || 1000) + svgPadding * 2;
      return { svgPadding, svgWidth, svgHeight };
    }, [imageSize.width, imageSize.height]);

    // Check if we should render anything
    const shouldRenderRooms = roomState.roomsVisible;
    const shouldRenderDimensions = roomState.showDimensions;

    // Return early if nothing should be rendered
    if (!shouldRenderRooms && !shouldRenderDimensions) return null;

    if (roomArray.length === 0) return null;

    return (
      <svg
        viewBox={`${-svgDimensions.svgPadding} ${-svgDimensions.svgPadding} ${svgDimensions.svgWidth} ${svgDimensions.svgHeight}`}
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          top: `${-svgDimensions.svgPadding}px`,
          left: `${-svgDimensions.svgPadding}px`,
          width: `${svgDimensions.svgWidth}px`,
          height: `${svgDimensions.svgHeight}px`,
          zIndex: 5, // Below markers (10) but above background image
          pointerEvents: 'auto',
        }}
      >
        {/* Instructions overlay removed - already shown in drawer */}

        {roomArray.map(room => {
          // Apply confidence filtering - same pattern as FixtureLayer
          const confidence = room.confidence ?? 1;
          const meetsConfidenceThreshold = confidence >= threshold;

          // Skip rendering if confidence is too low
          if (!meetsConfidenceThreshold) {
            return null;
          }

          // Use optimistic update if available, otherwise use original room data
          const displayRoom = optimisticRooms[room.id] || room;

          // Ensure points are parsed if they're strings
          let points = displayRoom.points;
          if (typeof points === 'string') {
            try {
              points = JSON.parse(points);
            } catch (e) {
              logger.error('Failed to parse room points:', e);
              return null;
            }
          }

          // Check if this room is being dragged
          const isBeingDragged =
            isDragging && dragState?.type === 'room' && dragState?.id === room.id;
          const isSelected = selectedRooms.has(room.id);
          const hasUnsavedChanges = !!pendingUpdatesRef.current[room.id];

          // If rooms are hidden but dimensions should show
          if (!shouldRenderRooms && shouldRenderDimensions && scaleData) {
            return (
              <RoomDimensions
                key={`dimensions-${room.id}`}
                points={points as any}
                scale={scale}
                scaleData={scaleData}
                showDimensions={true}
                showArea={roomState.showArea && shouldRenderDimensions} // Only show area with dimensions
                isSelected={false}
                opacity={0.9}
                offsetDistance={15}
                roomName={undefined}
                showRoomName={false}
                minimalMode={true}
              />
            );
          }

          // Normal room rendering with polygon (only show if rooms are visible)
          if (shouldRenderRooms) {
            // Get room type and color
            const roomType = (displayRoom as any).room_type || 'unknown';
            const roomColor = getRoomTypeColor(roomType);
            const roomLabel = formatRoomLabel(displayRoom.name, roomType);

            return (
              <RoomPolygon
                key={`${room.id}-${displayRoom.name}`}
                id={room.id}
                points={points as any}
                name={roomLabel}
                scale={scale}
                opacity={opacity}
                selected={isSelected}
                onSelect={e => handleRoomSelect(room.id, e)}
                onDoubleClick={() => handleRoomDoubleClick(room.id)}
                onPointDrag={handlePointDrag(room.id)}
                onAddPoint={handleAddPoint(room.id)}
                onRemovePoint={handleRemovePoint(room.id)}
                onMouseDown={handleRoomMouseDown(room.id)}
                isDragging={isBeingDragged}
                showLabel={true} // Always show label when room is visible
                hasUnsavedChanges={hasUnsavedChanges}
                scaleData={scaleData}
                showDimensions={roomState.showDimensions && scaleData !== null}
                showArea={roomState.showArea}
                fillColor={roomColor}
                strokeColor={roomColor}
                segmentationMask={(displayRoom as any).segmentation_mask}
                renderMode={(displayRoom as any).segmentation_mask ? 'both' : 'polygon'}
                maskQuality={(displayRoom as any).mask_quality}
              />
            );
          }

          return null;
        })}
      </svg>
    );
  },
);

RoomLayer.displayName = 'RoomLayer';
